import type { ReactNode } from "react";
import { breiteKlasse, cx, prozent } from "./fortschritt/breite";

/**
 * FortschrittBalken (Kantine Einkauf und Bedarf E1, Entscheidungsvorlage 2026-10-20: Rundgang 1A „12 von 30 erfasst“, Übersicht 2A „2 von 6
 * abgehakt“; Design-Guide `fortschritt-balken.md`, Musterkatalog M-118). Ein Balken mit Satz (links) und optionalem Zusatz (rechts), z. B. dem
 * Zeitpunkt „Rundgang begonnen 15:02“. Kein Ampelwert (dafür `Auslastung`), keine Prozentzahl im Text: der Satz nennt „n von m“.
 *
 * Farbe nur aus Theme-Tokens (`bg-primary`, Spur `bg-surface-1`), Breite über die festen Klassen `wp0`…`wp100` (kein `style=`). Rein darstellend.
 */
export function FortschrittBalken({
  wert,
  gesamt,
  satz,
  zusatz,
  label,
  groesse = "standard",
}: {
  wert: number;
  gesamt: number;
  /** „12 von 30 erfasst“ */
  satz: string;
  /** Rechts neben dem Satz, z. B. „Rundgang begonnen 15:02 · jede Wahl wird sofort gespeichert“. */
  zusatz?: ReactNode;
  /** Name des Balkens für Hilfstechnik. */
  label: string;
  /** `standard`: Satz über dem Balken (Kopf einer Fläche); `klein`: nur Balken mit Satz darunter (Karte). */
  groesse?: "standard" | "klein";
}) {
  const p = prozent(wert, gesamt);
  const balken = (
    <div
      className={groesse === "klein" ? "flex w-full h-1.5 bg-surface-1 rounded-full overflow-hidden" : "flex w-full h-2 bg-surface-1 rounded-full overflow-hidden"}
      role="progressbar"
      aria-label={label}
      aria-valuenow={wert}
      aria-valuemin={0}
      aria-valuemax={gesamt}
      aria-valuetext={satz}
    >
      <span className={cx("h-full rounded-full bg-primary", breiteKlasse(p))} />
    </div>
  );
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/goal-progress-bar-card (Balken mit Satz; Farbe als Theme-Token) */}
      {groesse === "klein" ? (
        <div className="min-w-0">
          {balken}
          <p className="mt-1 text-xs text-muted-foreground-1">{satz}</p>
        </div>
      ) : (
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap justify-between items-center gap-x-3 gap-y-1">
            <p className="text-sm text-foreground">{satz}</p>
            {zusatz && <p className="text-xs text-muted-foreground-1">{zusatz}</p>}
          </div>
          {balken}
        </div>
      )}
    </>
  );
}
